import { z } from 'zod'
import { EntityTypes } from '../enums/entityTypes'
import {
  PaddingSchema,
  MarginSchema,
  BorderWidthSchema,
  BorderColorSchema,
  BorderStyleSchema,
  BorderStyleValue,
  AlignSchema,
  AppearanceSchema,
} from './styleSchemas'

export const BaseEntitySchema = z.object({
  id: z.string(),
  type: z.enum(EntityTypes),
  appearance: AppearanceSchema.optional(),
})

export const ContainerEntitySchema = BaseEntitySchema.extend({
  childIds: z.array(z.string()),
})

export const BodySchema = ContainerEntitySchema.extend({
  type: z.literal(EntityTypes.Body),
})

export const SectionSchema = ContainerEntitySchema.extend({
  type: z.literal(EntityTypes.Section),
  padding: PaddingSchema,
  backgroundColor: z.string().nullable(),
})

export const ColumnSchema = ContainerEntitySchema.extend({
  type: z.literal(EntityTypes.Column),
  size: z.number(),
})

export const ButtonSchema = BaseEntitySchema.extend({
  type: z.literal(EntityTypes.Button),
  text: z.string(),
  backgroundColor: z.string().nullable(),
  fontSize: z.number(),
  lineHeight: z.number().optional(),
  fontFamily: z.string().optional(),
  fontWeightAndStyle: z.string().optional(),
  color: z.string(),
  url: z.string(),
  borderRadius: z.number(),
  padding: PaddingSchema,
  margin: MarginSchema,
  align: AlignSchema,
  borderWidth: BorderWidthSchema,
  borderColor: BorderColorSchema,
  borderStyle: BorderStyleSchema,
})

export const HorizontalLineSchema = BaseEntitySchema.extend({
  type: z.literal(EntityTypes.HorizontalLine),
  color: z.string(),
  borderWidth: z.number().min(1),
  style: BorderStyleValue,
  padding: PaddingSchema,
})

export const ImageSchema = BaseEntitySchema.extend({
  type: z.literal(EntityTypes.Image),
  fileId: z.number().nullable(),
  align: AlignSchema,
  url: z.string().optional(),
  width: z.number().optional(),
})

export const TextSchema = BaseEntitySchema.extend({
  type: z.literal(EntityTypes.Text),
  html: z.string(),
  padding: PaddingSchema,
})

export const EntitySchema = z.discriminatedUnion('type', [
  BodySchema,
  SectionSchema,
  ColumnSchema,
  ButtonSchema,
  HorizontalLineSchema,
  ImageSchema,
  TextSchema,
])

export const EntitySchemaMap = {
  [EntityTypes.Body]: BodySchema,
  [EntityTypes.Section]: SectionSchema,
  [EntityTypes.Column]: ColumnSchema,
  [EntityTypes.Button]: ButtonSchema,
  [EntityTypes.HorizontalLine]: HorizontalLineSchema,
  [EntityTypes.Image]: ImageSchema,
  [EntityTypes.Text]: TextSchema,
} as const

export type BaseEntityType = z.infer<typeof BaseEntitySchema>
export type ContainerEntityType = z.infer<typeof ContainerEntitySchema>
export type BodyType = z.infer<typeof BodySchema>
export type SectionType = z.infer<typeof SectionSchema>
export type ColumnType = z.infer<typeof ColumnSchema>
export type ButtonType = z.infer<typeof ButtonSchema>
export type HorizontalLineType = z.infer<typeof HorizontalLineSchema>
export type ImageType = z.infer<typeof ImageSchema>
export type TextType = z.infer<typeof TextSchema>

export type EntityType = z.infer<typeof EntitySchema>
